import { startCase } from 'lodash';

import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';

import {
  FieldValidationErrorObject,
  FormGroupControls,
  getControlErrors,
} from '../interfaces/form-error.interface';

export function getFormValidationErrors(
  controls: FormGroupControls,
  control_index: number | null = null
): FieldValidationErrorObject[] {
  let errors: FieldValidationErrorObject[] = [];

  Object.keys(controls).forEach(key => {
    const control = controls[key];
    const controlName = getControlName(control);

    // Iterate over Groups
    if (control instanceof FormGroup) {
      errors = errors.concat(getFormValidationErrors(control.controls));
    }
    // Iterate over Arrays
    if (control instanceof FormArray) {
      control.controls.forEach((e: AbstractControl, index) => {
        if (e instanceof FormGroup) {
          // An item's own validators (cross-field rules) live on the group.
          errors = errors.concat(getControlOwnErrors(e, index));
          errors = errors.concat(getFormValidationErrors(e.controls, index));
        } else {
          errors = errors.concat(
            getFieldValidationErrors(e as FormControl, controlName as string)
          );
        }
      });
    }

    errors = errors.concat(getControlOwnErrors(control, control_index));
  });
  return errors;
}

/** The errors set on `control` itself, without walking its children. */
export function getControlOwnErrors(
  control: AbstractControl,
  control_index: number | null = null
): FieldValidationErrorObject[] {
  const controlErrors: ValidationErrors | null = control.errors;
  if (controlErrors === null) {
    return [];
  }

  const controlName = getControlName(control);
  const parentKey = control.parent ? getControlName(control.parent) : '';

  return Object.keys(controlErrors).map(keyError => {
    const error: FieldValidationErrorObject = {
      control_name: controlName,
      parent_control: parentKey,
      isNested: Boolean(parentKey),
      error_name: keyError,
      error_value: controlErrors[keyError],
    };
    if (control_index !== null) {
      error.control_index = control_index;
    }
    error.default_message = getDefaultErrorMessage(error, control);
    return error;
  });
}

// Utiliy Functions

export function getFieldValidationErrors(
  control: FormControl,
  control_name: string | null
): FieldValidationErrorObject[] {
  const errors: FieldValidationErrorObject[] = [];
  const controlErrors: ValidationErrors | null = control.errors;

  if (controlErrors !== null) {
    Object.keys(controlErrors).forEach(keyError => {
      const error: FieldValidationErrorObject = {
        control_name,
        error_name: keyError,
        error_value: controlErrors[keyError],
      };
      error.default_message = getDefaultErrorMessage(error, control);
      errors.push(error);
    });
  }
  return errors;
}

export function getControlName(control: AbstractControl): string | null {
  const parent = control.parent;
  let parentName = '';
  let name = '';
  if (parent !== null) {
    if (parent instanceof FormArray) {
      parentName = getControlName(parent) as string;
      return parentName;
    } else {
      parentName = getControlName(parent) as string;
      Object.keys(parent.controls).forEach(key => {
        const childControl = parent.get(key);
        if (childControl !== control) {
          return;
        }
        name = parentName ? `${parentName}.${key}` : key;
      });
    }
  }
  return name;
}

function getDefaultErrorMessage(
  error: FieldValidationErrorObject,
  control: AbstractControl
): string {
  let text;
  let reqLen;

  let controlDisplayName = (error.control_name as string)
    .split('.')
    .map(part => startCase(part))
    .join(', ');

  switch (error.error_name) {
    case 'required':
      text = `${controlDisplayName} is a required field.`;
      break;
    case 'pattern':
      text = `${controlDisplayName} has invalid pattern.`;
      break;
    case 'email':
      text = `${controlDisplayName} has invalid email format.`;
      break;
    case 'maxlength':
    case 'minlength':
      reqLen = error.error_value.requiredLength;
      text = `${controlDisplayName}
      has an invalid length. Required ${error.error_name}:${reqLen}.`;
      break;
    case 'arrayMinLength':
      reqLen = error.error_value.requiredLength;
      text = `${controlDisplayName}
      should have at least ${reqLen} item${reqLen > 1 ? 's' : ''}.`;
      break;
    case 'arrayMaxLength':
      reqLen = error.error_value.requiredLength;
      text = `${controlDisplayName}
      should not have more than ${reqLen} item${reqLen > 1 ? 's' : ''}.`;
      break;
    default:
      if (Object.hasOwn(getControlErrors(control), error.error_name)) {
        text = getControlErrors(control)[error.error_name];
      } else {
        text = `${controlDisplayName} contains errors. (${error.error_name}).`;
      }
  }
  return _capitalize(text);
}

function _capitalize(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
